<template>
  <div class="login-container">
    <div class="login-card">
      <h2 class="login-title">Welcome</h2>
      <p class="login-subtitle">Sign in to your RTH Group account</p>

      <form @submit.prevent="handleLogin" class="login-form">
        <label>Email Address</label>
        <input type="email" v-model="email" placeholder="you@example.com" required />

        <label>Password</label>
        <div class="password-wrapper">
          <input
            :type="showPassword ? 'text' : 'password'"
            v-model="password"
            required
          />
          <button type="button" class="toggle-visibility" @click="showPassword = !showPassword">
            {{ showPassword ? '🙈' : '👁️' }}
          </button>
        </div>

        <div class="login-options">
          <label class="remember-me">
            <input type="checkbox" v-model="rememberMe" />
            Remember me
          </label>
          <router-link to="/forgot-password" class="forgot-link">Forgot your password?</router-link>
        </div>

        <button class="login-button" :disabled="loading">
          {{ loading ? 'Signing in...' : 'Sign in' }}
        </button>

        <p v-if="error" class="error-msg">{{ error }}</p>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { auth, db } from '@/firebase'
import { currentClaims, homeFor, safeRedirect } from '@/router/access'
import {
  signInWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  signOut
} from 'firebase/auth'
import { collection, query, where, getDocs, updateDoc } from 'firebase/firestore'
import Swal from 'sweetalert2'

const email = ref('')
const password = ref('')
const showPassword = ref(false)
const rememberMe = ref(false)
const error = ref('')
const loading = ref(false)
const router = useRouter()
const route = useRoute()
// Page the person was trying to open before being sent here (set by the router guard)
const redirectTo = safeRedirect(route.query.redirect)

// Already logged in: go where they were headed, or to their role's home page
onMounted(async () => {
  const claims = await currentClaims()
  if (claims && claims.access !== 'blocked' && claims.access !== 'removed') {
    router.replace(redirectTo || homeFor(claims.role))
  }
})

const handleLogin = async () => {
  loading.value = true
  error.value = ''
  try {
    // Set auth persistence
    await setPersistence(
      auth,
      rememberMe.value ? browserLocalPersistence : browserSessionPersistence
    )

    // Sign in
    const userCred = await signInWithEmailAndPassword(
      auth,
      email.value,
      password.value
    )
    const uid = userCred.user.uid

    // Query Firestore users collection by uid field first
    let q = query(
      collection(db, 'users'),
      where('uid', '==', uid)
    )
    let snap = await getDocs(q)
    
    // If not found by uid, try searching by email (fallback for users without uid set yet)
    if (snap.empty) {
      q = query(
        collection(db, 'users'),
        where('email', '==', email.value)
      )
      snap = await getDocs(q)
    }

    // Newer records store emails lowercase; try that too
    if (snap.empty) {
      q = query(
        collection(db, 'users'),
        where('email', '==', email.value.trim().toLowerCase())
      )
      snap = await getDocs(q)
    }
    
    if (snap.empty) {
      // Firebase already signed them in; don't leave a session open without an account record
      await signOut(auth)
      throw new Error('ACCOUNT_NOT_FOUND')
    }

    const userDocRef = snap.docs[0].ref
    const userData = snap.docs[0].data()

    if (userData.accessStatus === 'blocked') {
      await signOut(auth)
      throw new Error('ACCOUNT_BLOCKED')
    }
    if (userData.accessStatus === 'removed') {
      // Same message an unregistered email gets, so a removed person can't tell
      await signOut(auth)
      throw Object.assign(new Error('ACCOUNT_REMOVED'), { code: 'auth/invalid-credential' })
    }

    // Update status to 'logged in' and set uid if not already set
    await updateDoc(userDocRef, { 
      status: 'logged in',
      uid: uid,  // Ensure uid is always set
      lastLogin: new Date() 
    })

    // Back to the page they asked for, if any (the router guard re-checks their role there)
    if (redirectTo) {
      router.push(redirectTo)
      return
    }

    // Determine role and redirect
    const role = userData.role
    if (role === 'admin' || role === 'superadmin') {
      router.push({ name: 'AdminDashboard' })
    } else if (role === 'payroll') {
      router.push({ name: 'AdminDashboard' })
    } else if (role === 'user' || role === 'applicant') {
      router.push({ name: 'MyApplications' })
    } else if (role === 'employee') {
      router.push({ name: 'MyPayStubs' })
    } else {
      throw new Error('NO_ROLE_ASSIGNED')
    }
  } catch (err) {
    console.error('Login error:', err)
    
    // Map error codes to user-friendly messages
    let errorTitle = 'Login Failed'
    let errorMessage = 'An unexpected error occurred. Please try again.'
    let errorIcon = 'error'

    if (err.message === 'ACCOUNT_NOT_FOUND') {
      errorTitle = 'Account Not Found'
      errorMessage = 'Your account hasn\'t been created yet. Please contact your administrator to set up your account.'
    } else if (err.message === 'ACCOUNT_BLOCKED' || err.code === 'auth/user-disabled') {
      errorTitle = 'Account Disabled'
      errorMessage = 'Your account has been disabled. Please contact RTH Group.'
    } else if (err.message === 'NO_ROLE_ASSIGNED') {
      errorTitle = 'Access Denied'
      errorMessage = 'Your account hasn\'t been assigned a role yet. Please contact your administrator.'
    } else if (err.code === 'auth/user-not-found') {
      errorTitle = 'Email Not Found'
      errorMessage = 'This email address is not registered. Please check your email or sign up for a new account.'
    } else if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
      errorTitle = 'Invalid Credentials'
      errorMessage = 'The password you entered is incorrect. Please try again or reset your password.'
    } else if (err.code === 'auth/invalid-email') {
      errorTitle = 'Invalid Email'
      errorMessage = 'Please enter a valid email address.'
    } else if (err.code === 'auth/too-many-requests') {
      errorTitle = 'Too Many Attempts'
      errorMessage = 'Your account has been temporarily disabled due to too many login attempts. Please try again later or reset your password.'
    } else if (err.code === 'auth/operation-not-allowed') {
      errorTitle = 'Service Unavailable'
      errorMessage = 'Email/password sign-in is currently disabled. Please contact support.'
    }

    // Show SweetAlert popup
    Swal.fire({
      title: errorTitle,
      text: errorMessage,
      icon: errorIcon,
      confirmButtonColor: '#1d4ed8',
      confirmButtonText: 'OK',
      allowOutsideClick: false,
      allowEscapeKey: false
    })

    error.value = errorMessage
  } finally {
    loading.value = false
  }
}
</script>


<style scoped>
.login-container {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #f1f5f9;
}

.login-card {
  background: #ffffff;
  padding: 2rem;
  width: 100%;
  max-width: 400px;
  border-radius: 10px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
}

.login-title {
  font-size: 1.75rem;
  text-align: center;
  margin-bottom: 0.25rem;
  font-weight: 700;
}

.login-subtitle {
  font-size: 0.9rem;
  color: #555;
  text-align: center;
  margin-bottom: 1.5rem;
}

.login-form {
  display: flex;
  flex-direction: column;
}

.login-form label {
  margin-bottom: 0.25rem;
  font-weight: 500;
  font-size: 0.9rem;
}

.login-form input[type="email"],
.login-form input[type="password"],
.password-wrapper input {
  padding: 0.65rem 0.75rem;
  margin-bottom: 1rem;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 1rem;
  width: 100%;
  box-sizing: border-box;
}

.password-wrapper {
  position: relative;
}

.toggle-visibility {
  position: absolute;
  right: 10px;
  top: 8px;
  background: none;
  border: none;
  font-size: 1rem;
  cursor: pointer;
}

.login-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
  font-size: 0.875rem;
}

.remember-me {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.forgot-link {
  color: #1d4ed8;
  text-decoration: none;
}

.forgot-link:hover {
  text-decoration: underline;
}

.login-button {
  background-color: #1d4ed8;
  color: #fff;
  font-weight: bold;
  border: none;
  padding: 0.75rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.3s ease;
}

.login-button:hover {
  background-color: #2563eb;
}

.login-button:disabled {
  background-color: #93c5fd;
  cursor: not-allowed;
}

.error-msg {
  margin-top: 1rem;
  color: red;
  text-align: center;
  font-size: 0.9rem;
}
</style>
